import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { TFunction } from 'i18next';
import { SnackbarKey, useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useDevices, useVideo } from 'hooks';
import { isPermissionDenied } from 'utils';

type MediaContainerProps = {
  children?: React.ReactNode;
};

export function getSnackbarContent(t: TFunction, hasAudio: boolean, hasVideo: boolean, error?: Error) {
  let headline = '';
  let message = '';

  switch (true) {
    // These custom errors are thrown by the useLocalTracks hook. They are thrown when the user explicitly denies
    // permission to only their camera, or only their microphone.
    case error?.message === 'CameraPermissionsDenied':
      headline = t('mediaUnableToAccess');
      message = t('mediaCameraPermDenied');
      break;
    case error?.message === 'MicrophonePermissionsDenied':
      headline = t('mediaUnableToAccess');
      message = t('mediaMicPermDenied');
      break;

    // This error is emitted when the user or the user's system has denied permission to use the media devices
    case error?.name === 'NotAllowedError':
      headline = t('mediaUnableToAccess');
      // Chrome only: system-level block
      message = error!.message === 'Permission denied by system' ? t('mediaOsBlocked') : t('mediaBothPermDenied');
      break;

    // This error is emitted when input devices are not connected or disabled in the OS settings
    case error?.name === 'NotFoundError':
      headline = t('mediaCannotFind');
      message = t('mediaNotFound');
      break;

    // Other getUserMedia errors are less likely to happen in this app. Here we will display
    // the system's error message directly to the user.
    case Boolean(error):
      headline = t('mediaAcquireError');
      message = `${error!.name} ${error!.message}`;
      break;

    case !hasAudio && !hasVideo:
      headline = t('mediaNoCameraOrMicFound');
      message = t('mediaNoCameraOrMicMessage');
      break;

    case !hasVideo:
      headline = t('mediaNoCameraFound');
      message = t('mediaNoCameraMessage');
      break;

    case !hasAudio:
      headline = t('mediaNoMicFound');
      message = t('mediaNoMicMessage');
      break;
  }

  return { headline, message };
}

const RETRY_BUTTON_SX = { color: 'inherit', fontWeight: 700, ml: 1, flexShrink: 0 };

const PERMISSION_ERROR_MESSAGES = new Set([
  'NotAllowedError',
  'CameraPermissionsDenied',
  'MicrophonePermissionsDenied',
]);

function isPermissionError(error?: Error) {
  if (!error) return false;
  return PERMISSION_ERROR_MESSAGES.has(error.name) || PERMISSION_ERROR_MESSAGES.has(error.message);
}

export function MediaContainer({ children }: MediaContainerProps) {
  const { t } = useTranslation();
  const { getAudioAndVideoTracks, isAcquiringLocalTracks } = useVideo();
  const { hasAudioInputDevices, hasVideoInputDevices } = useDevices();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const [mediaError, setMediaError] = useState<Error>();
  const [isSnackbarDismissed, setIsSnackbarDismissed] = useState(true);

  useEffect(() => {
    if (!mediaError) {
      getAudioAndVideoTracks()
        .catch((error) => {
          setMediaError(error);
        })
        .finally(() => {
          setIsSnackbarDismissed(false);
        });
    }
  }, [getAudioAndVideoTracks, mediaError]);

  const handleRetry = useCallback(
    async (snackbarKey: SnackbarKey) => {
      closeSnackbar(snackbarKey);
      const cameraDenied = await isPermissionDenied('camera');
      const micDenied = await isPermissionDenied('microphone');

      if (cameraDenied || micDenied) {
        enqueueSnackbar(t('mediaBrowserBlocked'), {
          variant: 'warning',
          persist: true,
          action: (key: SnackbarKey) => (
            <Button size='small' sx={RETRY_BUTTON_SX} onClick={() => handleRetry(key)}>
              {t('mediaTryAgain')}
            </Button>
          ),
        });
      } else {
        setIsSnackbarDismissed(true);
        setMediaError(undefined);
      }
    },
    [closeSnackbar, enqueueSnackbar],
  );

  useEffect(() => {
    const isSnackbarOpen =
      !isSnackbarDismissed &&
      !isAcquiringLocalTracks &&
      (Boolean(mediaError) || !hasAudioInputDevices || !hasVideoInputDevices);

    const { headline, message } = getSnackbarContent(t, hasAudioInputDevices, hasVideoInputDevices, mediaError);

    const showRetry = isPermissionError(mediaError);

    let snackbarKey: SnackbarKey = '';
    if (isSnackbarOpen) {
      snackbarKey = enqueueSnackbar(`${headline} ${message}`, {
        variant: 'error',
        persist: showRetry,
        action: showRetry
          ? (key: SnackbarKey) => (
              <Button size='small' sx={RETRY_BUTTON_SX} onClick={() => handleRetry(key)}>
                {t('mediaTryAgain')}
              </Button>
            )
          : undefined,
      });
    }
    return () => {
      closeSnackbar(snackbarKey);
    };
  }, [mediaError, hasAudioInputDevices, hasVideoInputDevices, handleRetry]);

  return <>{children}</>;
}
